import { useState } from "react";
import type { SaidaRota } from "../../lib/trpc.js";
import { DetalheTecnico } from "../../components/DetalheTecnico.js";
import { Area, Botao, Campo, Falha } from "../../components/ui.js";
import { AnaliseDaFoto } from "./AnaliseDaFoto.js";

type Item = SaidaRota["lotes"]["getById"]["itens"][number];

/**
 * "Gerar outra", do jeito do suporte: olhando o que deu errado antes.
 *
 * Gerar de novo com o mesmo prompt repete o mesmo erro quando o erro está no
 * prompt. Aqui o admin vê a imagem reprovada ao lado da original, o motivo da
 * falha, o que a análise leu na foto e o prompt que foi enviado — e corrige o
 * texto antes de mandar. Quem opera não passa por esta tela: gera direto com o
 * que a análise escreveu, e o servidor recusa um prompt vindo de outro papel.
 *
 * A outra saída é refazer do início: o prompt de um item antigo foi escrito pela
 * instrução anterior, e corrigir o texto à mão não traz a instrução nova. Aí a
 * foto passa pela análise de novo e o prompt é escrito outra vez.
 */
export function RegerarComPrompt({
  item,
  gerando,
  refazendo,
  falha,
  aoGerar,
  aoRefazer,
  aoCancelar,
}: {
  item: Item;
  gerando: boolean;
  refazendo: boolean;
  /** Aqui dentro, e não no topo da página: a página está atrás da sobreposição. */
  falha: string | null;
  aoGerar: (prompt: string) => void;
  aoRefazer: () => void;
  aoCancelar: () => void;
}) {
  const [prompt, setPrompt] = useState(item.prompt ?? "");

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Referência, não o assunto: no desktop as duas fotos ocupavam metade da
          altura e empurravam o prompt — que é o que se veio editar — para baixo. */}
      <div className="grid grid-cols-2 gap-2 sm:max-w-md">
        <figure className="flex flex-col gap-1.5">
          <img
            src={item.urls.original}
            alt=""
            className="aspect-[4/5] w-full rounded-peca bg-superficie-alta object-contain"
          />
          <figcaption className="text-[0.75rem] text-texto-fraco">Foto original</figcaption>
        </figure>
        <figure className="flex flex-col gap-1.5">
          {item.urls.gerada ? (
            <img
              src={item.urls.gerada}
              alt=""
              className="aspect-[4/5] w-full rounded-peca bg-superficie-alta object-contain"
            />
          ) : (
            <div className="aspect-[4/5] w-full rounded-peca border border-borda bg-superficie-alta" />
          )}
          <figcaption className="text-[0.75rem] text-texto-fraco">
            {item.urls.gerada ? "A que saiu" : "Não chegou a gerar"}
          </figcaption>
        </figure>
      </div>

      {item.erro && item.estado !== "recusado" && (
        <section className="flex flex-col gap-2 rounded-peca border border-descartar/40 bg-descartar/10 px-4 py-3">
          <p className="text-[0.8125rem] leading-snug text-texto wrap-anywhere">{item.erro}</p>
          <DetalheTecnico detalhe={item.erroTecnico} />
        </section>
      )}

      <AnaliseDaFoto descricao={item.analise?.descricao ?? {}} />

      <Campo
        rotulo="Prompt"
        dica={
          item.analise?.promptOrigem === "montado"
            ? "Montado pelo sistema — o modelo de visão não escreveu um para esta foto."
            : "O que foi enviado ao modelo de imagem. “Gerar com este prompt” usa este texto; “Refazer do início” analisa a foto de novo e escreve outro."
        }
      >
        {/* Cresce com o texto, sem limite: o painel já rola, e rolagem dentro de
            rolagem é onde o fim do prompt se perdia no celular. `rows` é o
            tamanho para quem não tem `field-sizing`. */}
        <Area
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          maxLength={10_000}
          rows={14}
          className="max-h-none!"
        />
      </Campo>

      {/* Preso ao pé do painel: com o prompt inteiro à vista, o botão de gerar
          fica sempre ao alcance — e a falha, se houver, aparece junto dele. */}
      <div className="sticky bottom-0 -mx-4 -mb-4 flex flex-col gap-3 border-t border-borda bg-fundo px-4 py-3">
        {falha && <Falha mensagem={falha} />}
        <div className="flex flex-wrap gap-2">
          <Botao
            tom="brasa"
            carregando={gerando}
            disabled={prompt.trim() === ""}
            onClick={() => aoGerar(prompt)}
          >
            Gerar com este prompt
          </Botao>
          <Botao tom="quieto" carregando={refazendo} disabled={gerando} onClick={aoRefazer}>
            Refazer do início
          </Botao>
          <Botao tom="fantasma" onClick={aoCancelar}>
            Cancelar
          </Botao>
        </div>
      </div>
    </div>
  );
}
